import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, Search } from "lucide-react";
import { Sidebar } from "./Sidebar";
import { sectionFor } from "./sections";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/authStore";
import { usePageTitle } from "@/hooks/usePageTitle";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { GlobalSearch } from "@/components/search/GlobalSearch";
const ROUTE_TITLES = {
    "/": "Start",
    "/alerts": "Alerts",
    "/sounds": "Sounds & Videos",
    "/channelpoints": "Kanalpunkte",
    "/overlay": "Szenen & Overlays",
    "/stopwatch": "Stoppuhr",
    "/countdown": "Countdown",
    "/sandbox": "Sandbox",
    "/commands": "Befehle",
    "/timers": "Timer",
    "/counters": "Zähler",
    "/moderation": "Moderation",
    "/chatlogs": "Chat-Log",
    "/events": "Event-Historie",
    "/points": "Punkte",
    "/requests": "Wünsche",
    "/casino-settings": "Casino",
    "/admin/casino": "Casino · Spieler",
    "/lootbox": "Lootbox",
    "/songrequests": "Song Requests",
    "/orgchart": "Organigramm",
    "/discord": "Discord",
    "/kofi": "Ko-fi",
    "/settings": "Einstellungen",
};
function SectionTabs({ pathname }) {
    const section = sectionFor(pathname);
    if (!section)
        return null;
    return (
    // Inset shadow instead of border-b — see components/ui/tabs.tsx.
    _jsx("nav", { className: "mb-6 flex overflow-x-auto overflow-y-hidden shadow-[inset_0_-1px_0_hsl(var(--border))]", "aria-label": "Bereich", children: section.tabs.map((t) => (_jsx(NavLink, { to: t.to, className: ({ isActive }) => cn("shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 transition-colors", isActive
                ? "border-brand text-brand"
                : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted"), children: t.label }, t.to))) }));
}
export function DashboardLayout() {
    const activeChannel = useAuthStore((s) => s.activeChannel);
    const location = useLocation();
    usePageTitle(ROUTE_TITLES[location.pathname] ?? "Dashboard");
    // Phones: the sidebar is a drawer behind the ☰ button and closes again as
    // soon as a page is picked. From md up it is the fixed column as before.
    const [menuOpen, setMenuOpen] = useState(false);
    useEffect(() => setMenuOpen(false), [location.pathname]);
    // Global search: Ctrl/Cmd+K anywhere, "/" when not typing somewhere.
    const [searchOpen, setSearchOpen] = useState(false);
    useEffect(() => {
        const onKey = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setSearchOpen((o) => !o);
                return;
            }
            if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey)
                return;
            const t = e.target;
            if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)))
                return;
            e.preventDefault();
            setSearchOpen(true);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);
    return (
    // dvh, not vh: on phones 100vh includes the area behind the browser's
    // address bar, which cut off the bottom of every page.
    _jsxs("div", { className: "flex h-dvh dark bg-background text-foreground", children: [_jsx(Sidebar, { open: menuOpen, onClose: () => setMenuOpen(false) }), _jsx(GlobalSearch, { open: searchOpen, onClose: () => setSearchOpen(false) }), _jsxs("main", { className: "crisio-headings min-w-0 flex-1 overflow-auto", children: [_jsxs("div", { className: "sticky top-0 z-20 flex items-center gap-2 border-b bg-background/95 px-4 py-2 text-xs backdrop-blur md:px-6", children: [_jsx("button", { onClick: () => setMenuOpen(true), className: "-ml-1 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground md:hidden", "aria-label": "Men\u00FC \u00F6ffnen", children: _jsx(Menu, { className: "h-5 w-5" }) }), _jsx("span", { className: "min-w-0 truncate font-semibold md:hidden", children: ROUTE_TITLES[location.pathname] ?? "" }), _jsxs("button", { onClick: () => setSearchOpen(true), className: "ml-auto flex shrink-0 items-center gap-2 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground sm:w-56 sm:border sm:px-3 sm:py-1.5 md:ml-0", "aria-label": "Suchen", children: [_jsx(Search, { className: "h-5 w-5 sm:h-3.5 sm:w-3.5" }), _jsx("span", { className: "hidden sm:inline", children: "Suchen \u2026" }), _jsx("kbd", { className: "ml-auto hidden rounded border px-1 font-mono text-[10px] sm:inline", children: "Strg K" })] }), _jsx("span", { className: "hidden text-muted-foreground sm:inline md:ml-auto", children: "Aktiver Channel:" }), _jsx("span", { className: "shrink-0 rounded-md bg-primary px-2 py-0.5 font-semibold text-primary-foreground", children: activeChannel?.displayName ?? "—" })] }), _jsxs("div", { className: "p-4 md:p-6", children: [_jsx(SectionTabs, { pathname: location.pathname }), _jsx(ErrorBoundary, { scope: location.pathname, children: _jsx(Outlet, {}) }, location.pathname)] })] })] }));
}
